import { PlatformLocation } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MvdModalService } from 'app/shared/services/modal/mvd-modal.service';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';
import { AddressSourceType, IAddressSourceType } from '../customer/models/enums/address-source-type.enum';
import { Country, ICountry } from '../Foundation/models/enums/country.enum';
import { County } from '../foundation/models/enums/county.enum';
import { IJurisdiction, Jurisdiction } from '../foundation/models/enums/jurisdiction.enum';
import { LandcategoryType } from '../foundation/models/enums/landcategory-type.enum';
import { MilitaryCityPrefixType } from '../foundation/models/enums/military-city-prefix-type.enum';
import { MilitaryZipCode } from '../foundation/models/enums/military-zip-code.enum';
import { BaseComponent } from '../shared/base-components/base.component';
import { ZipCodePipe } from '../shared/pipes/zip-code.pipe';
import { ModalSize } from '../shared/services/modal/mvd-modal.enum';
import { ControlValidationMessagesService } from '../shared/validators/control-validation-messages.service';
import { EmissionAreaType } from '../titleregistration/models/enums/emission-area-type.enum';
import { AddressEmissionAreaTypeModalComponent } from './address-modal-emission-area-type/address-emission-area-type-modal.component';
import { Address } from './models/address.model';
import { AddressType } from './models/enums/address-type.enum';
import { AddressService } from './services/address.service';
import { ZipCodeEmission } from './models/zipcode-emission.model';

export class AddressModalContext {
    constructor(
        public addressNumberId: number,
        public entityId: number,
        public componentTypeName: string = '',
        public isWip: boolean = false,
        public canSetUndeliverable: boolean = false,
        public isACPResidential: boolean = false,
        public useOriginalWhenMatched: boolean = false,
        public addressData: Address = null,
        public isIRPEdit: boolean = false
    ) { }
}

@Component({
    selector: 'address-modal',
    templateUrl: './address-modal.html'
})
export class AddressModalComponent extends BaseComponent implements OnInit {
    public addressData: Address = new Address();
    public addressForm: UntypedFormGroup;
    public addressSources: IAddressSourceType[] = new AddressSourceType().GetActive();
    public addressTypeName = '';
    public addressTypes: any[];
    public addressTypesData = new AddressType();
    public addressNumberId: number;
    public busy = false;
    public canSetUndeliverable: boolean;
    public componentTypeName: string;
    public counties: any[];
    public countries: ICountry[];
    public countryData = new Country();
    public displayAddress = '';
    public displayValidatedAddress = '';
    public emissionsAreaType = new EmissionAreaType();
    public emissionsAreaTypeIdChanged = false;
    public isJurisdictionApplicable = !this.data.isACPResidential;
    public isWip: boolean;
    public isIRPEdit: boolean;
    public matched = true;
    public matchingMessage = '';
    public militaryCities: any[];
    public militaryJurisdictions: any[];
    public militaryZipCodes: any[];
    public nonMatchLatLongEmissionTypeValue = -1;
    public nullValue = null;
    public showCountyInput = true;
    public showValidationSelection = false;
    public validationTypes: { [key: string]: number } = {
        useCorrected: 1,
        useCurrent: 2,
        tryAgain: 3,
    };

    private cities: string[] = [];
    private countriesWithJurisdictions = [this.countryData.US.id, this.countryData.MX.id, this.countryData.CA.id, this.countryData.DE.id, this.countryData.TW.id, this.countryData.KR.id];
    private countyData = new County();
    private defaultEmissionAreaTypeId: number;
    private statesData = new Jurisdiction();
    private landcategoryTypeData = new LandcategoryType();
    private militaryCityData = new MilitaryCityPrefixType();
    private militaryJurisdictionData = new Jurisdiction();
    private militaryZipCodeData = new MilitaryZipCode();
    private poBoxes: string[] = [];
    private rawCities = 'APO,DPO,FPO,NONE,Unknown,UNK';
    private rawPoBoxes = 'po box,p.o. box,p box,pobox,po#,po #,POBOX,BX,POSTOFFICE,POSTOFICE,POSTOFCE,PSTOFFICE,PSTOFFICED,PSTOFICEDR,POSTOFCEDR,POSTOFICED,DRAWER,DRAWR,PODRAWER,PODRAWR,GENERALDEL,GENDELIVER,GENDILIVER,GENDEL,GENDELIV,General Delivery,Rural Route,Rural Free Delivery,RFD,Highway Contract Route,HC Route,Mail Stop Code, MSC ,Private Mailbox Number,PMB,Star Route';
    private showValidationProcessComplete = false;
    private validatedAddress: Address;
    private zipCodeEmissions: ZipCodeEmission[] = [];

    constructor(
        private location: PlatformLocation,
        public dialogRef: MatDialogRef<AddressModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: AddressModalContext,
        private addressService: AddressService,
        private modalService: MvdModalService,
        private fb: UntypedFormBuilder,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.location.onPopState(() => this.dialogRef.close());
        this.setPropertiesFromContextData();
        this.loadLookups();
        this.createAddressForm();
        this.getAddress();
        this.poBoxes = this.processPoBoxes(this.rawPoBoxes);
        this.cities = this.rawCities.split(',')
            .map(c => c.trim().toLowerCase());
    }

    private getAddress(): void {
        if (this.addressNumberId > 0) {
            this.addressService.getAddress(this.addressNumberId)
                .pipe(
                    map(result => result ? result : new Address()),
                    tap(() => this.addressForm.get('addressTypeId').disable())
                )
                .subscribe(result => {
                    this.populateAddressData(result);
                    this.setInitialValidators();
                    this.setValidationRules();
                    this.initValueChangesSubscriptions();
                });
        } else if (!!this.data.addressData) {
            this.populateAddressData(this.addressData);
            this.setInitialValidators();
            this.setValidationRules();
            this.initValueChangesSubscriptions();
        } else {
            this.populateAddressDataWithDefaults();
            this.setInitialValidators();
            this.initValueChangesSubscriptions();
        }
    }

    private populateAddressDataWithDefaults(): void {
        this.addressData = new Address();
        this.addressData.addressNumberId = 0;
        this.addressData.entityId = this.data.entityId;
        this.loadAddressTypes();

        this.addressForm.get('countryId').setValue(this.countryData.US.id, {emitEvent: false});
        this.addressForm.get('jurisdictionId').setValue(this.statesData.AZ.id, {emitEvent: false});
        this.isJurisdictionApplicable = true;
    }

    private populateAddressData(address: Address): void {
        this.addressData = address;
        this.loadAddressTypes();
        this.addressForm.get('addressTypeId').setValue(this.addressData.addressTypeId);

        if (this.data.isACPResidential) {
            this.clearFieldsForACPResidential();
        } else {
            this.addressForm.get('addressLine1').setValue(this.addressData.addressLine1);
            this.addressForm.get('addressLine2').setValue(this.addressData.addressLine2);
            this.addressForm.get('addressSourceTypeId').setValue(this.addressData.addressSourceTypeId);
            this.addressForm.get('emissionAreaTypeId').setValue(this.addressData.emissionAreaTypeId);
            this.addressForm.get('inCareOf').setValue(this.addressData.inCareOf);
            this.addressForm.get('internationalJurisdiction').setValue(this.addressData.internationalJurisdiction, {emitEvent: false});
            this.addressForm.get('isMilitary').setValue(this.addressData.isMilitary, {emitEvent: false});
            this.addressForm.get('isUndeliverable').setValue(this.addressData.isUndeliverable);

            // Map the customerId,vehicleId,biz transactionId to address depending on what was sent in
            const entityIdCandidates = [this.addressData.customerNumberId, this.addressData.vehicleId, this.addressData.businessTransactionItemKeyId];
            const entityId = entityIdCandidates.find(e => e != null);
            if (entityId) {
                this.addressData.entityId = entityId;
            }

            const countryId = !!this.addressData.countryId ? this.addressData.countryId : this.countryData.US.id;
            this.addressForm.get('countryId').setValue(countryId, {emitEvent: false});
            this.isJurisdictionApplicable = this.countriesWithJurisdictions.includes(countryId);

            if (this.addressData.isMilitary) {
                this.initializeMilitaryData();
            } else {
                this.addressForm.get('city').setValue(this.addressData.city);
                this.addressForm.get('countyId').setValue(this.addressData.countyId);
                this.addressForm.get('jurisdictionId').setValue(this.addressData.jurisdictionId, {emitEvent: false});
                this.addressForm.get('postalZipCode').setValue(this.addressData.postalZipCode);
            }
        }
    }

    private clearFieldsForACPResidential(): void {
        this.addressData.updatedDate = '';

        this.addressForm.get('addressLine1').reset('');
        this.addressForm.get('addressLine2').reset('');
        this.addressForm.get('addressSourceTypeId').reset();
        this.addressForm.get('city').reset('');
        this.addressForm.get('countryId').reset();
        this.addressForm.get('countyId').reset();
        this.addressForm.get('emissionAreaTypeId').reset();
        this.addressForm.get('jurisdictionId').reset();
        this.addressForm.get('postalZipCode').reset('');
    }

    private loadLookups(): void {
        this.countries = this.countryData.GetActive();
        this.counties = this.countyData.GetActive();
        this.militaryCities = this.militaryCityData.GetActive();
        this.militaryZipCodes = this.militaryZipCodeData.GetActive().sort((a, b) => (+a.value) - (+b.value));
        this.militaryJurisdictions = this.statesData.GetActive().filter(s => s.isMilitary);

        this.addressService.getZipCodeEmissions().subscribe(
            result => {
                if (result) {
                    this.zipCodeEmissions = result.result;
                }
            },
            error => this.handleValidationResult(error)
        );
    }

    private setPropertiesFromContextData(): void {
        this.addressNumberId = this.data.addressNumberId;
        this.componentTypeName = this.data.componentTypeName;
        this.isWip = this.data.isWip;
        this.canSetUndeliverable = this.data.canSetUndeliverable;
        this.showCountyInput = this.data.componentTypeName != 'oneTimeMailing';
        this.addressData = this.data.addressData;
        this.isIRPEdit = this.data.isIRPEdit;
    }

    /**
     * Get address types for dropdowns based on input
     */
    private loadAddressTypes = (): void => {
        this.addressTypes = new Array();
        const name = this.componentTypeName.toLocaleLowerCase();

        if (this.addressData.addressNumberId == 0 || this.addressData.addressNumberId == null) {
            if (name.indexOf('individual-customer') > -1) {
                this.addressData.sosOptOut = true;
                if (name.replace('individual-customer', '') == '-physical') {
                    this.addressTypes.push({ 'id': this.addressTypesData.Physical.id, 'displayValue': 'Residential' });
                    this.addressForm.get('addressTypeId').setValue(this.addressTypesData.Physical.id);
                    this.addressTypeName = 'residential';
                } else if (name.replace('individual-customer', '') == '-mailing') {
                    this.addressTypes.push({ 'id': this.addressTypesData.Mailing.id, 'displayValue': this.addressTypesData.Mailing.displayValue });
                    this.addressForm.get('addressTypeId').setValue(this.addressTypesData.Mailing.id);
                    this.addressTypeName = 'mailing';
                } else {
                    this.addressTypes.push({ 'id': this.addressTypesData.Physical.id, 'displayValue': 'Residential' });
                    this.addressTypes.push({ 'id': this.addressTypesData.Mailing.id, 'displayValue': this.addressTypesData.Mailing.displayValue });
                }
            } else if (name.indexOf('organization-customer') > -1) {
                if (name.replace('organization-customer', '') == '-physical') {
                    this.addressTypes.push({ 'id': this.addressTypesData.Physical.id, 'displayValue': 'Commercial' });
                    this.addressForm.get('addressTypeId').setValue(this.addressTypesData.Physical.id);
                } else if (name.replace('organization-customer', '') == '-mailing') {
                    this.addressTypes.push({ 'id': this.addressTypesData.Mailing.id, 'displayValue': this.addressTypesData.Mailing.displayValue });
                    this.addressForm.get('addressTypeId').setValue(this.addressTypesData.Mailing.id);
                } else {
                    this.addressTypes.push({ 'id': this.addressTypesData.Physical.id, 'displayValue': 'Commercial' });
                    this.addressTypes.push({ 'id': this.addressTypesData.Mailing.id, 'displayValue': this.addressTypesData.Mailing.displayValue });
                }

                this.addressTypeName = 'commercial';
            } else if (name == 'domicile') {
                this.addressTypes.push({ 'id': this.addressTypesData.Domicile.id, 'displayValue': this.addressTypesData.Domicile.displayValue });
                this.addressForm.get('addressTypeId').setValue(this.addressTypesData.Domicile.id);
                this.addressTypeName = 'vehicle domicile';
            } else if (name == 'onetimemailing') {
                this.addressTypes.push({ 'id': this.addressTypesData.OneTimeMailing.id, 'displayValue': this.addressTypesData.OneTimeMailing.displayValue });
                this.addressForm.get('addressTypeId').setValue(this.addressTypesData.OneTimeMailing.id);
                this.addressTypeName = 'one time mailing';
            } else if (name == 'motorcarriermailing') {
                this.addressTypes.push({ 'id': this.addressTypesData.MotorCarrierMailing.id, 'displayValue': this.addressTypesData.MotorCarrierMailing.displayValue });
                this.addressForm.get('addressTypeId').setValue(this.addressTypesData.MotorCarrierMailing.id);
                this.addressTypeName = 'motor carrier mailing';
            }
        } else {
            let displayValue = this.getEnumValues(this.addressData.addressTypeId, this.addressTypesData);
            if (this.addressTypesData.Physical.id == this.addressData.addressTypeId) {
                if (name.indexOf('individual-customer') > -1) {
                    displayValue = 'Residential';
                    this.addressTypeName = 'residential';
                } else if (name.indexOf('organization-customer') > -1) {
                    displayValue = 'Commercial';
                    this.addressTypeName = 'commercial';
                }
            }

            if (this.addressTypesData.Mailing.id == this.addressData.addressTypeId) {
                if (name.indexOf('motorcarriermailing') > -1) {
                    displayValue = 'Motor Carrier Mailing';
                    this.addressTypeName = 'motor carrier mailing';
                }
            }

            if (this.addressData.addressTypeId != this.addressTypesData.Correspondence.id) {
                this.addressTypes.push({ 'id': this.addressData.addressTypeId, 'displayValue': displayValue });
            }
        }
    };

    public filterStates = (): IJurisdiction[] => {
        const activeStates = this.statesData.GetActive();
        const countryId = this.addressForm.get('countryId').value;

        return activeStates.filter(s => !s.isMilitary && s.country == countryId);
    };

    /**
     * Initialize military data
     */
    private initializeMilitaryData = (): void => {
        try {
            const zip: string = this.addressData.postalZipCode ? this.addressData.postalZipCode.trim() : null;
            if (zip) {
                const militaryZip = this.militaryZipCodeData.FromValue(zip);

                this.addressForm.get('militaryCityPrefixTypeId').setValue(militaryZip.militaryCityPrefixTypeId, {emitEvent: false});
                this.addressForm.get('militaryJurisdictionId').setValue(militaryZip.jurisdictionId, {emitEvent: false});
                this.addressForm.get('militaryZipCode').setValue(militaryZip.id, {emitEvent: false});
            }
        } catch (Exception) {
            // Ignore the exception?
        }
    };

    /**
     * Create form controls.
     */
    private createAddressForm = (): void => {
        this.addressForm = this.fb.group({
            'addressLine1': ['', Validators.maxLength(40)],
            'addressLine2': ['', Validators.maxLength(40)],
            'addressSourceTypeId': [null],
            'addressTypeId': [null, [Validators.required, ControlValidationMessagesService.requireSelectedItem]],
            'city': [''],
            'countryId': [null],
            'countyId': [null],
            'emissionAreaTypeId': [null],
            'inCareOf': ['', Validators.maxLength(50)],
            'internationalJurisdiction': ['', Validators.maxLength(30)],
            'isMilitary': [null],
            'isUndeliverable': [null],
            'jurisdictionId': [null],
            'militaryCityPrefixTypeId': [null],
            'militaryJurisdictionId': [null],
            'militaryZipCode': [''],
            'postalZipCode': [''],
            'validationType': [null],
            'isEstablishedPlaceOfBusiness': [null]
        });
    };

    /**
     * Set and save the undeliverable bool to true.
     */
    public setUndeliverable = (): void => {
        const isUndeliverable = !this.addressForm.get('isUndeliverable').value;
        this.addressService.setUndeliverable(this.addressNumberId, isUndeliverable)
            .subscribe(r => this.dialogRef.close(this.getModel()));
    };

    private onAddressTypeIdChange = (addressTypeId: number): void => {
        this.setAddressSourceTypeIdValidators();
        this.setAddressLine1Validators();
        this.setJurisdictionIdValidators();
        this.setCountryIdValidators();
        this.setCityValidators();
        if (addressTypeId != this.addressTypesData.Mailing.id) {
            this.addressForm.get('isMilitary').reset(false);
        }
    };

    /**
     * Set country on model after dropdown change.
     * @param countryId
     */
    private onCountryIdChange = (countryId: number): void => {
        this.isJurisdictionApplicable = this.countriesWithJurisdictions.includes(countryId);
        this.addressForm.get('countyId').reset();
        this.addressForm.get('jurisdictionId').reset();
        this.setCountyIdValidators();
        this.setValidationRules();
    };

    private onInternationalJurisdictionChange = (): void => {
        this.addressForm.get('militaryJurisdictionId').setValue(null);
    };

    /**
     * Updates the layout of the modal based on whether the address is a military address
     **/
    private onIsMilitaryChange = (isMilitary: boolean): void => {
        if (isMilitary) {
            this.addressData.landCategoryTypeId = this.landcategoryTypeData.Military.id;
            this.addressData.geocodeConfidenceRatingTypeId = null;
            this.addressData.addressConfidenceRatingTypeId = null;

            this.addressForm.get('city').reset('');
            this.addressForm.get('countyId').reset();
            this.addressForm.get('postalZipCode').reset('');
            this.addressForm.get('jurisdictionId').reset();

            this.addressForm.get('militaryCityPrefixTypeId').setValidators(Validators.required);
            this.addressForm.get('militaryJurisdictionId').setValidators(Validators.required);
            this.addressForm.get('militaryZipCode').setValidators(Validators.required);
        } else {
            this.addressData.landCategoryTypeId = null;
            this.addressForm.get('emissionAreaTypeId').reset();
            const jurisdictionId = this.addressForm.get('countryId').value == this.countryData.US.id ? this.statesData.AZ.id : null;
            this.addressForm.get('jurisdictionId').reset(jurisdictionId);
            this.addressForm.get('militaryCityPrefixTypeId').reset();
            this.addressForm.get('militaryJurisdictionId').reset();
            this.addressForm.get('militaryZipCode').reset('');

            this.addressForm.get('militaryCityPrefixTypeId').clearValidators();
            this.addressForm.get('militaryJurisdictionId').clearValidators();
            this.addressForm.get('militaryZipCode').clearValidators();
        }

        this.addressForm.get('militaryCityPrefixTypeId').updateValueAndValidity({emitEvent: false});
        this.addressForm.get('militaryJurisdictionId').updateValueAndValidity({emitEvent: false});
        this.addressForm.get('militaryZipCode').updateValueAndValidity({emitEvent: false});

        this.setCityValidators();
        this.setJurisdictionIdValidators();
        this.setPostalZipCodeValidators();
    };

    private onJurisdictionIdChange = (jurisdictionId: number): void => {
        if (jurisdictionId) {
            this.addressForm.get('militaryJurisdictionId').reset();
            this.addressForm.get('internationalJurisdiction').reset();
        }

        if (this.statesData.AZ.id != jurisdictionId) {
            this.addressForm.get('countyId').reset();
        }

        this.setCountyIdValidators();
        this.setCityValidators();
    };

    /**
     * update military data with zip
     * @param militaryZipCode
     */
    private onMilitaryZipCodeChange = (militaryZipCode: number): void => {
        if (militaryZipCode) {
            const militaryZip = this.militaryZipCodeData.FromId(militaryZipCode);
            this.addressForm.get('militaryCityPrefixTypeId').setValue(militaryZip.militaryCityPrefixTypeId);
            this.addressForm.get('militaryJurisdictionId').setValue(militaryZip.jurisdictionId);
        }
    };

    /**
     *Event to change the emission area type to exempt(3)
     */
    private onValidationTypeChange = (validationType: number): void => {
        if (validationType == this.validationTypes.useCorrected) {
            this.addressForm.get('emissionAreaTypeId').reset(null);
            return;
        }

        let emissionAreaTypeId: number;
        if (this.addressForm.get('addressTypeId').value == this.addressTypesData.Mailing.id || this.addressForm.get('addressTypeId').value == this.addressTypesData.OneTimeMailing.id) {
            // Determine what the emissionAreaTypeId would be off of the entered ZipCode. If it can't be determined or it's partially exempt, then set it to exempt.
            const matchingRecord = this.zipCodeEmissions.find(z => z.postalZipCode.trim() == this.addressForm.get('postalZipCode').value);
            if (matchingRecord) {
                if (!!matchingRecord.partiallyExempt || !!matchingRecord.totallyExempt) {
                    emissionAreaTypeId = this.emissionsAreaType.EmissionsExempt.id;
                } else {
                    const emissionArea = matchingRecord.area == null ? '' : matchingRecord.area.trim();
                    emissionAreaTypeId = emissionArea == this.emissionsAreaType.A.value ?
                        this.emissionsAreaType.A.id : emissionArea == this.emissionsAreaType.B.value ?
                            this.emissionsAreaType.B.id : this.emissionsAreaType.EmissionsExempt.id;
                }
            } else {
                emissionAreaTypeId = this.emissionsAreaType.EmissionsExempt.id;
            }
        } else {
            // Set the emissionAreaTypeId to default and let the CSR choose the emission type
            emissionAreaTypeId = this.nonMatchLatLongEmissionTypeValue;
        }

        this.addressForm.get('emissionAreaTypeId').setValue(emissionAreaTypeId);
    };

    private cityDeniedValidator = (formControl: UntypedFormControl): ValidationErrors | null => {
        if (!formControl.value) {
            return null;
        }

        const cityLowercase = formControl.value.toLowerCase();
        if (this.cities.some(c => cityLowercase == c)) {
            return { cityDenied: true };
        }

        return null;
    };

    private mustBeAZValidator = (formControl: UntypedFormControl): ValidationErrors | null => {
        if (formControl.value != this.statesData.AZ.id) {
            return { mustBeAZ: true };
        }

        return null;
    };

    private mustBeEmptyValidator = (formControl: UntypedFormControl): ValidationErrors | null => {
        if (formControl.value) {
            return { 'mustBeEmpty': true };
        }

        return null;
    };

    private mustBeUSValidator = (formControl: UntypedFormControl): ValidationErrors | null => {
        if (formControl.value != this.countryData.US.id) {
            return { mustBeUS: true };
        }

        return null;
    };

    private poBoxValidator = (formControl: UntypedFormControl): ValidationErrors | null => {
        const addressLine1 = formControl.value;
        if (addressLine1) {
            // Bug #108665 - If AddressLine1 has the letters MSC in it with a space before or after should not allow.
            const addressLowerCase = addressLine1.toLowerCase();
            if (this.poBoxes.some(p => addressLowerCase.includes(p))) {
                return { poBoxDenied: true };
            }
        }

        return null;
    };

    private setAddressLine1Validators = (): void => {
        const validators = [Validators.required, Validators.maxLength(40)];
        if (this.addressForm.get('addressTypeId').value == this.addressTypesData.Physical.id || this.addressForm.get('addressTypeId').value == this.addressTypesData.Domicile.id) {
            validators.push(this.poBoxValidator);
        }

        this.addressForm.get('addressLine1').setValidators(validators);
        this.addressForm.get('addressLine1').updateValueAndValidity({emitEvent: false});
    };

    private setAddressSourceTypeIdValidators = (): void => {
        const validators = [];
        const addressTypeId = this.addressForm.get('addressTypeId').value;
        if (addressTypeId == this.addressTypesData.Physical.id || addressTypeId == this.addressTypesData.Mailing.id) {
            validators.push(Validators.required);
        }

        this.addressForm.get('addressSourceTypeId').setValidators(validators);
        this.addressForm.get('addressSourceTypeId').updateValueAndValidity({emitEvent: false});
    };

    private setCityValidators = (): void => {
        const validators = [];
        if (!this.addressForm.get('isMilitary').value) {
            validators.push(Validators.maxLength(26));
            validators.push(Validators.required);

            if (this.addressForm.get('addressTypeId').value != this.addressTypesData.Physical.id && this.addressForm.get('addressTypeId').value != this.addressTypesData.Domicile.id) {
                validators.push(this.cityDeniedValidator);
            }
        }

        this.addressForm.get('city').setValidators(validators);
        this.addressForm.get('city').updateValueAndValidity({emitEvent: false});
    };

    private setCountryIdValidators = (): void => {
        const validators = [Validators.required, ControlValidationMessagesService.requireSelectedItem];
        if (this.addressForm.get('addressTypeId').value == this.addressTypesData.Domicile.id) {
            validators.push(this.mustBeUSValidator);
        }

        this.addressForm.get('countryId').setValidators(validators);
        this.addressForm.get('countryId').updateValueAndValidity({emitEvent: false});
    };

    private setCountyIdValidators = (): void => {
        const validators = [];
        if (!this.addressForm.get('isMilitary').value && this.showCountyInput && this.isJurisdictionApplicable) {
            const jurisdictionId = this.addressForm.get('jurisdictionId').value;
            const validator = jurisdictionId == this.statesData.AZ.id ? Validators.required : this.mustBeEmptyValidator;
            validators.push(validator);
        }

        this.addressForm.get('countyId').setValidators(validators);
        this.addressForm.get('countyId').updateValueAndValidity({emitEvent: false});
    };

    private initValueChangesSubscriptions(): void {
        this.addressForm.get('addressTypeId').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onAddressTypeIdChange(v));
        this.addressForm.get('countryId').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onCountryIdChange(v));
        this.addressForm.get('internationalJurisdiction').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onInternationalJurisdictionChange());
        this.addressForm.get('isMilitary').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onIsMilitaryChange(v));
        this.addressForm.get('jurisdictionId').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onJurisdictionIdChange(v));
        this.addressForm.get('militaryZipCode').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onMilitaryZipCodeChange(v));
        this.addressForm.get('validationType').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.onValidationTypeChange(v));
    }

    private setInitialValidators(): void {
        this.setAddressLine1Validators();
        this.setAddressSourceTypeIdValidators();
        this.setCityValidators();
        this.setCountyIdValidators();
        this.setCountryIdValidators();
        this.setJurisdictionIdValidators();
        this.setPostalZipCodeValidators();
    }

    private setJurisdictionIdValidators = (): void => {
        const validators = [];
        if (!this.addressForm.get('isMilitary').value) {
            validators.push(Validators.required);
            if (this.addressForm.get('addressTypeId').value == this.addressTypesData.Domicile.id) {
                validators.push(this.mustBeAZValidator);
            }
        }

        this.addressForm.get('jurisdictionId').setValidators(validators);
        this.addressForm.get('jurisdictionId').updateValueAndValidity({emitEvent: false});
    };

    private setPostalZipCodeValidators(): void {
        const validators = [];
        if (!this.addressForm.get('isMilitary').value) {
            validators.push(Validators.required, Validators.maxLength(10));
        }

        this.addressForm.get('postalZipCode').setValidators(validators);
        this.addressForm.get('postalZipCode').updateValueAndValidity({emitEvent: false});
    }

    /**
     * Cancel address change and close modal.
     */
    public cancel = (): void => {
        this.dialogRef.close();
    };

    /**
     * Continue after view address validation data.
     */
    public continue = (): void => {
        const validationType = this.addressForm.get('validationType').value;
        this.showValidationSelection = false;
        switch (validationType) {
            case this.validationTypes.useCorrected:
                this.integrateValidatedAddress(this.validatedAddress);
                // intentionally not breaking here
            case this.validationTypes.useCurrent:
                this.addressData.isOveridden = false;
                this.showValidationProcessComplete = true;
                this.addressForm.get('isUndeliverable').setValue('false');
                this.saveAddress();
                break;
            default:
                this.showValidationProcessComplete = false;
        }
    };

    public save = (): void => {
        this.validatedAddress = null;
        if (this.addressForm.get('countryId').value != this.countryData.US.id || this.addressForm.get('isMilitary').value) {
            this.saveAddress();
        } else if (!this.addressForm.get('isMilitary').value) {
            this.busy = true;
            this.addressService.validateZipCode(this.addressForm.get('postalZipCode').value).subscribe(
                () => {
                    this.validationResults = null;
                    this.saveAddress();
                },
                err => {
                    this.handleValidationResult(err);
                    // Check if the validation error message string is "Invalid Zipcode" and set an error flag on the postalZipCode field in addressForm.
                    if (err.error && err.error.validationResults && err.error.validationResults[0] && err.error.validationResults[0].errorMessage == 'Invalid Zipcode') {
                        this.addressForm.get('postalZipCode').setErrors({ 'incorrect': true });
                    }
                    this.busy = false;
                }
            );
        }
    };

    /**
     * Process address save if valid and validation is complete.
     */
    private saveAddress = (): void => {
        if (!this.addressForm.valid) {
            return;
        }

        if (!this.isJurisdictionApplicable){
            this.addressForm.get('jurisdictionId').reset();
        }

        const isCountryVerificationNeeded = null == this.addressForm.get('countryId').value || this.countryData.US.id == this.addressForm.get('countryId').value;
        const isAddressVerificationNeeded = !this.addressForm.get('isMilitary').value && !this.showValidationProcessComplete && isCountryVerificationNeeded;

        this.busy = true;
        if (isAddressVerificationNeeded) {
            this.addressService.validateAddress(this.getModel()).subscribe(r => {
                this.busy = false;
                this.matched = r.matched;
                this.matchingMessage = r.matchingMessage;

                let validationType: number;
                if (!r.matched) {
                    validationType = this.validationTypes.tryAgain;
                    this.displayAddress = this.getAddressDisplayString(this.getModel());
                    this.showValidationSelection = true;
                } else if (r.viewCorrections) {
                    // Show the user corrections and ask if they are okay with them.
                    validationType = this.validationTypes.useCorrected;

                    // Copy data
                    this.validatedAddress = new Address();
                    this.copyAddressData(this.validatedAddress, r.validatedAddress);
                    this.setEnumValues(this.validatedAddress);
                    this.displayAddress = this.getAddressDisplayString(this.getModel());
                    this.displayValidatedAddress = this.getAddressDisplayString(this.validatedAddress);
                    this.showValidationSelection = true;
                } else {
                    // Everything is good save without showing user anything.
                    validationType = this.validationTypes.useCurrent;

                    // Copy data
                    this.integrateValidatedAddress(r.validatedAddress);
                    this.validatedAddress = new Address();
                    this.copyAddressData(this.validatedAddress, r.validatedAddress);
                    this.setEnumValues(this.validatedAddress);
                    this.addressData.isOveridden = false;
                    this.showValidationSelection = false;
                    this.showValidationProcessComplete = true;
                    this.saveAddress();
                }

                this.addressForm.get('validationType').setValue(validationType, {emitEvent: false});
                if (this.addressForm.get('addressTypeId').value == this.addressTypesData.Physical.id || this.addressForm.get('addressTypeId').value == this.addressTypesData.Domicile.id) {
                    this.defaultEmissionAreaTypeId = r.defaultEmissionAreaTypeId;
                    if (this.defaultEmissionAreaTypeId) {
                        this.addressForm.get('emissionAreaTypeId').setValue(r.defaultEmissionAreaTypeId);
                    }
                }
            });
        } else if (!this.isWip) {
            this.addressService.saveAddress(this.getModel())
                .pipe(finalize(() => this.busy = false))
                .subscribe(
                    r => this.dialogRef.close(r),
                    e => this.handleValidationResult(e)
                );
        } else {
            this.dialogRef.close(this.getModel());
        }
    };

    /**
     * Copies the data from an address address into the form
     * @param validatedAddress the address
     */
    private integrateValidatedAddress(validatedAddress: Address): void {
        if (!validatedAddress) {
            return;
        }

        if (!this.data.useOriginalWhenMatched) {
            this.addressForm.get('addressLine1').setValue(validatedAddress.addressLine1);
            this.addressForm.get('addressLine2').setValue(validatedAddress.addressLine2);
            this.addressForm.get('city').setValue(validatedAddress.city);
            this.addressForm.get('postalZipCode').setValue(validatedAddress.postalZipCode);
            this.addressForm.get('countryId').setValue(validatedAddress.countryId, { emitEvent: false });
        }

        this.addressForm.get('addressTypeId').setValue(validatedAddress.addressTypeId, {emitEvent: false});
        this.addressForm.get('countyId').setValue(validatedAddress.countyId);
        this.addressForm.get('emissionAreaTypeId').setValue(validatedAddress.emissionAreaTypeId);
        this.addressForm.get('inCareOf').setValue(validatedAddress.inCareOf);
        this.addressForm.get('isUndeliverable').setValue(false);
        this.addressForm.get('jurisdictionId').setValue(validatedAddress.jurisdictionId);
        this.addressForm.get('isEstablishedPlaceOfBusiness').setValue(validatedAddress.isEstablishedPlaceOfBusiness);
    }

    private getAddressDisplayString(address: Address): string {
        var displayAddress: string = address.addressLine1;
        displayAddress += this.getValueWithSpaceOrEmpty(address.addressLine2);
        displayAddress += this.getValueWithSpaceOrEmpty(address.city);
        displayAddress += this.getValueWithSpaceOrEmpty(address.county);
        displayAddress += this.getValueWithSpaceOrEmpty(address.state, false);
        displayAddress += ' ' + (new ZipCodePipe()).transform(address.postalZipCode);
        return displayAddress;
    }

    private getValueWithSpaceOrEmpty(value: string, withComma: boolean = true): string {
        if (value) {
            return ` ${value}` + (withComma ? ',' : '');
        }
        return '';
    }

    private getEnumValues(id: number, types: any): string {
        if (!!id && !!types && !!types.FromId) {
            const typeResult = types.FromId(id);
            if (typeResult) {
                return (types == this.statesData) ? typeResult.code : typeResult.displayValue;
            }
        }

        return '';
    }

    public getEmissionsAreaTypeDisplayValue(emissionAreaTypeId: number): string {
        const emissionsAreaType = this.emissionsAreaType.FromId(emissionAreaTypeId);

        return emissionsAreaType == null ? '' : emissionsAreaType.displayValue;
    }

    public hasValidationError(controlName: string, ruleName: string): boolean {
        return this.addressForm.get(controlName).dirty &&
            this.addressForm.get(controlName).touched &&
            this.addressForm.get(controlName).hasError(ruleName);
    }

    private setValidationRules(): void {
        const countryId = this.addressForm.get('countryId').value;
        this.countriesWithJurisdictions.includes(countryId) ? this.addressForm.get('jurisdictionId').enable() : this.addressForm.get('jurisdictionId').disable();
        this.countryData.US.id == countryId ? this.addressForm.get('countyId').enable() : this.addressForm.get('countyId').disable();
    }

    public openEmissionAreaTypeModal(): void {
        this.modalService
            .open(AddressEmissionAreaTypeModalComponent, { addressNumberId: this.addressData.addressNumberId, emissionAreaTypeId: this.addressForm.get('emissionAreaTypeId').value }, ModalSize.Medium)
            .afterClosed()
            .subscribe(
                emissionAreaTypeId => {
                    if (this.addressForm.get('emissionAreaTypeId').value != emissionAreaTypeId) {
                        this.emissionsAreaTypeIdChanged = true;
                        this.addressForm.get('emissionAreaTypeId').setValue(emissionAreaTypeId);
                    }
                }
            );
    }

    private processPoBoxes(rawPoBoxes: string): string[] {
        return rawPoBoxes.split(',')
            .map(p => {
                p = p.toLowerCase();

                return p == ' msc ' ? p : p.trim();
            });
    }

    /**
     * Copy the source address to the target data
     * @param target
     * @param source
     */
    private copyAddressData = (target: Address, source: Address): void => {
        target.addressNumberId = source.addressNumberId;
        target.id = source.id;
        target.addressTypeId = source.addressTypeId;
        target.jurisdictionId = source.jurisdictionId;
        target.countyId = source.countyId;
        target.countryId = source.countryId;
        target.isMilitary = source.isMilitary;
        target.inCareOf = source.inCareOf;
        target.addressLine1 = source.addressLine1;
        target.addressLine2 = source.addressLine2;
        target.latitude = source.latitude;
        target.longitude = source.longitude;
        target.city = source.city;
        target.postalZipCode = source.postalZipCode;
        target.postalDeliveryCode = source.postalDeliveryCode;
        target.isUndeliverable = false;
        target.undeliverableDate = source.undeliverableDate;
        target.isPreferredContactMethod = source.isPreferredContactMethod;
        target.nonMilitaryJurisdictionId = source.nonMilitaryJurisdictionId;
        target.militaryJurisdictionId = source.militaryJurisdictionId;
        target.militaryCityPrefixTypeId = source.militaryCityPrefixTypeId;
        target.emissionAreaTypeId = source.emissionAreaTypeId;
        target.addressConfidenceRatingTypeId = source.addressConfidenceRatingTypeId;
        target.geocodeConfidenceRatingTypeId = source.geocodeConfidenceRatingTypeId;
        target.landCategoryTypeId = source.landCategoryTypeId;
        target.updatedBy = source.updatedBy;
        target.updatedDate = source.updatedDate;
        target.viewId = source.viewId;
        target.addressType = source.addressType;
        target.county = source.county;
        target.country = source.country;
        target.state = source.state;
        target.customerNumberId = source.customerNumberId;
        target.vehicleId = source.vehicleId;
        target.businessTransactionItemKeyId = source.businessTransactionItemKeyId;
        target.isEstablishedPlaceOfBusiness = source.isEstablishedPlaceOfBusiness;

        this.setEnumValues(target);
    };

    private getModel(): any {
        const model = Object.assign(new Address(), this.addressData);
        if (this.validatedAddress) {
            this.copyAddressData(model, this.validatedAddress);
            if (this.data.isACPResidential) {
                model.customerNumberId = this.addressData.customerNumberId;
            }
        }

        // Form Data
        model.addressLine1 = this.addressForm.get('addressLine1').value;
        model.addressLine2 = this.addressForm.get('addressLine2').value;
        model.addressSourceTypeId = this.addressForm.get('addressSourceTypeId').value || undefined;
        model.addressTypeId = this.addressForm.get('addressTypeId').value;
        model.countyId = this.addressForm.get('countyId').value || undefined;
        model.countryId = this.addressForm.get('countryId').value;
        model.emissionAreaTypeId = this.addressForm.get('emissionAreaTypeId').value || undefined;
        model.inCareOf = this.addressForm.get('inCareOf').value;
        model.internationalJurisdiction = this.addressForm.get('internationalJurisdiction').value;
        model.isMilitary = this.addressForm.get('isMilitary').value || undefined;
        model.isUndeliverable = this.addressForm.get('isUndeliverable').value || undefined;
        model.militaryCityPrefixTypeId = this.addressForm.get('militaryCityPrefixTypeId').value || undefined;
        model.militaryJurisdictionId = this.addressForm.get('militaryJurisdictionId').value || undefined;
        model.militaryZipCode = this.addressForm.get('militaryZipCode').value;
        model.isEstablishedPlaceOfBusiness = this.addressForm.get('isEstablishedPlaceOfBusiness').value || undefined;

        if (model.isMilitary) {
            const militaryCityType = this.militaryCityData.FromId(this.addressForm.get('militaryCityPrefixTypeId').value);
            const militaryJurisdictionType = this.militaryJurisdictionData.FromId(this.addressForm.get('militaryJurisdictionId').value);
            const militaryZipCodeType = this.militaryZipCodeData.FromId(this.addressForm.get('militaryZipCode').value);

            model.city = militaryCityType ? militaryCityType.value : undefined;
            model.jurisdictionId = militaryJurisdictionType ? militaryJurisdictionType.id : undefined;
            model.postalZipCode = militaryZipCodeType ? militaryZipCodeType.value : undefined;
        } else {
            model.city = this.addressForm.get('city').value;
            model.jurisdictionId = this.addressForm.get('jurisdictionId').value || undefined;
            model.postalZipCode = this.addressForm.get('postalZipCode').value;
        }

        this.setEnumValues(model);

        return model;
    }

    private setEnumValues = (address: Address): void => {
        address.addressType = this.getEnumValues(address.addressTypeId, this.addressTypesData);
        address.county = this.getEnumValues(address.countyId, this.countyData);
        address.country = this.getEnumValues(address.countryId, this.countryData);
        address.state = this.getEnumValues(address.jurisdictionId, this.statesData);
    };
}
